/**
 * Better-Auth client for frontend authentication
 *
 * This client handles all authentication operations:
 * - Sign in/sign up
 * - Session management
 * - Two-factor authentication
 * - Password reset
 * - Organization management
 *
 * Usage:
 *   import { authClient } from '@/lib/auth-client';
 *   const session = await authClient.getSession();
 */

import { createAuthClient } from "better-auth/react";
import { phoneNumberClient, twoFactorClient } from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";
import { createORPCAPIClient } from "@better-bookkeeping/auth-client";
import { createInvitationClient } from "@better-bookkeeping/auth-client/invitations";

declare global {
  interface Window {
    ENV?: { API_URL?: string };
  }
}

// Get API URL from environment.
//
// Auth UI and API share the same origin in every deployment — caddy routes
// `/api*` to the API container and everything else to this UI. Defaulting
// to `window.location.origin` keeps the client correct in test/staging/prod
// without per-environment build-time variables. Build-time `VITE_API_URL`
// and runtime `window.ENV.API_URL` still override (used by local dev where
// the UI dev server runs on a different port than the API).
//
// SSR fallback (no `window`): `localhost:3001` for local dev only — prod
// builds don't run in SSR mode here.
export const AUTH_API_URL =
  typeof window !== "undefined"
    ? window.ENV?.API_URL || import.meta.env.VITE_API_URL || window.location.origin
    : import.meta.env.VITE_API_URL || "http://localhost:3001";

export const authClient = createAuthClient({
  baseURL: AUTH_API_URL,
  // Session cookie name (must match server config)
  sessionCookieName: "better-auth.session_token",
  // Disable Better Auth's default redirect plugin — it redirects to /login
  // on unauthenticated getSession() calls, which strips query params like
  // redirect_uri. The gateway handles its own routing via TanStack Router.
  disableDefaultFetchPlugins: true,
  // Enable credentials for cross-origin requests
  fetchOptions: {
    credentials: "include",
  },
  plugins: [passkeyClient(), twoFactorClient(), phoneNumberClient()],
});

/** oRPC API client — speaks the RPCHandler wire protocol for typed procedure calls */
export const orpcClient = createORPCAPIClient({ baseUrl: AUTH_API_URL });
export const invitationClient = createInvitationClient({ baseUrl: AUTH_API_URL });

/**
 * Type exports for Better-Auth session and user
 */
export type Session = typeof authClient.$Infer.Session;
export type User = Session["user"];
